import { Link } from 'react-router-dom';
import { Star, Heart } from 'lucide-react';
import { useRef, useEffect } from 'react';
import gsap from 'gsap';
import { useFavorites } from '../contexts/FavoritesContext';
import { useCart } from '../contexts/CartContext';

export default function ProductCard({ product }) {
  const cardRef = useRef(null);
  const imageRef = useRef(null);
  const { toggleFavorite, isFavorite } = useFavorites();
  const { addToCart } = useCart();
  
  const isProductFavorite = isFavorite(product.id);
  
  const handleFavoriteClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    toggleFavorite(product);
  };
  
  const handleAddToCart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    addToCart(product, 1);
  };

  useEffect(() => {
    const card = cardRef.current;
    const image = imageRef.current;

    const handleMouseEnter = () => {
      gsap.to(card, {
        y: -8,
        boxShadow: '0 10px 30px -10px rgba(242, 122, 26, 0.3)',
        duration: 0.3,
        ease: 'power2.out',
      });
      gsap.to(image, {
        scale: 1.08,
        duration: 0.4,
        ease: 'power2.out',
      });
    };

    const handleMouseLeave = () => {
      gsap.to(card, {
        y: 0,
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        duration: 0.3,
        ease: 'power2.out',
      });
      gsap.to(image, {
        scale: 1,
        duration: 0.4,
        ease: 'power2.out',
      });
    };

    card.addEventListener('mouseenter', handleMouseEnter);
    card.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      card.removeEventListener('mouseenter', handleMouseEnter);
      card.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, []);

  return (
    <div
      ref={cardRef}
      className="group bg-white rounded-xl overflow-hidden border border-gray-200 cursor-pointer relative"
      data-testid={`product-card-${product.id}`}
    >
      <Link to={`/urun/${product.id}`}>
        <div className="relative aspect-[3/4] overflow-hidden bg-gray-100">
          <img
            ref={imageRef}
            src={product.image}
            alt={product.name}
            className="w-full h-full object-cover"
            loading="lazy"
            decoding="async"
          />
          {product.isNew && (
            <span className="absolute top-2 left-2 bg-trendyol-orange text-white text-xs font-bold px-2 py-1 rounded">
              YENİ
            </span>
          )}
          {product.isFreeShipping && (
            <span className="absolute bottom-2 left-2 bg-white text-trendyol-orange text-xs font-bold px-2 py-1 rounded shadow-md">
              KARGO BEDAVA
            </span>
          )}
        </div>
      </Link>
      
      <button
        onClick={handleFavoriteClick}
        className="absolute top-2 right-2 z-10 bg-white/90 hover:bg-white p-2 rounded-full shadow-md transition-all"
        data-testid={`favorite-button-${product.id}`}
      >
        <Heart 
          className={`w-4 h-4 ${isProductFavorite ? 'fill-red-500 text-red-500' : 'text-gray-600'}`}
        />
      </button>

      <Link to={`/urun/${product.id}`}>
        <div className="p-3">
          <h3 className="font-medium text-sm text-gray-900 mb-1 line-clamp-2 h-10">
            {product.name}
          </h3>

          <div className="flex items-center gap-1 mb-2">
            <div className="flex">
              {[...Array(5)].map((_, i) => (
                <Star
                  key={i}
                  className={`w-3 h-3 ${
                    i < Math.floor(product.rating)
                      ? 'fill-yellow-400 text-yellow-400'
                      : 'text-gray-300'
                  }`}
                />
              ))}
            </div>
            <span className="text-xs text-gray-600">({product.reviews})</span>
          </div>

          <div className="flex items-baseline gap-2">
            <span className="font-manrope font-bold text-lg text-trendyol-orange">
              {product.price.toFixed(2)} TL
            </span>
            {product.originalPrice && (
              <span className="text-xs text-gray-400 line-through">
                {product.originalPrice.toFixed(2)} TL
              </span>
            )}
          </div>

          <p className="text-xs text-gray-500 mt-1">{product.seller}</p>
        </div>
      </Link>
    </div>
  );
}
